import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner';
import { apiFetch } from '@/lib/api';
import { productionTypeMeta } from '@/lib/labels';
import { getJson, getQueryData, setQueryData, useQuery } from '@/lib/query';
import { cn } from '@/lib/utils';

interface SidebarProject {
  id: string;
  title: string;
  production_type?: string;
  owner_email?: string;
}

interface ProjectsPage {
  projects?: SidebarProject[];
  next_cursor?: string;
}

interface RailData {
  items: SidebarProject[];
  nextCursor: string;
}

const PAGE = 40;
const SEARCH_DEBOUNCE_MS = 300;

const railKey = (q: string) => `sidebar:projects:${q}`;
const fetchPage = (q: string, cursor = '') =>
  getJson<ProjectsPage>(() =>
    apiFetch(
      `/api/projects?limit=${PAGE}` +
        (q ? `&q=${encodeURIComponent(q)}` : '') +
        (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''),
    ),
  );

/** Refresh the first page without dropping further pages already loaded. */
async function refreshRail(q: string): Promise<RailData> {
  const page = await fetchPage(q);
  const fresh = page.projects || [];
  const prev = getQueryData<RailData>(railKey(q));
  if (!prev || prev.items.length <= PAGE) return { items: fresh, nextCursor: page.next_cursor || '' };
  const ids = new Set(fresh.map((p) => p.id));
  return { items: [...fresh, ...prev.items.filter((p) => !ids.has(p.id))], nextCursor: prev.nextCursor };
}

/** Persistent project rail — project navigation is always one click away. */
export default function AppSidebar() {
  const nav = useNavigate();
  const loc = useLocation();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const { t } = useTranslation();

  // Search is server-side (projects are paged), so debounce the keystrokes.
  useEffect(() => {
    const id = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // Cached per search term: switching projects never refetches the rail.
  const { data, refetch, isLoading } = useQuery<RailData>(railKey(search), () => refreshRail(search), {
    staleMs: 5 * 60_000,
  });
  const projects = useMemo(() => data?.items ?? [], [data]);
  const nextCursor = data?.nextCursor ?? '';

  const activeId = loc.pathname.match(/\/projects\/([^/]+)/)?.[1];
  const atProjectsRoot = loc.pathname === '/projects';
  // True when we've navigated into a project the rail has never heard of — i.e. one
  // that was just created. That, and returning to the list, are the only moments the
  // set of projects can have changed (renames/deletes happen from the list).
  const unknownProject = !!data && !search && !!activeId && activeId !== 'new' && !projects.some((p) => p.id === activeId);

  useEffect(() => {
    if (atProjectsRoot || unknownProject) refetch().catch(() => {});
  }, [atProjectsRoot, unknownProject, refetch]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchPage(search, nextCursor);
      setQueryData<RailData>(railKey(search), (prev) => {
        const items = prev?.items ?? [];
        const ids = new Set(items.map((p) => p.id));
        return { items: [...items, ...(page.projects || []).filter((p) => !ids.has(p.id))], nextCursor: page.next_cursor || '' };
      });
    } catch {
      /* the button stays available to retry */
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, search]);

  return (
    <aside className="flex w-64 shrink-0 flex-col border-r border-border bg-elevated">
      <div className="space-y-3 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-faint" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('sidebar.search')}
            className={cn(
              'w-full rounded-md border border-border bg-card py-2 pl-8 pr-2.5 text-[12.5px]',
              'outline-none transition-colors placeholder:text-faint focus:border-primary',
            )}
          />
        </div>
        <Button size="sm" className="w-full" onClick={() => nav('/projects/new')}>
          <Plus className="mr-1.5 h-4 w-4" /> {t('sidebar.newProject')}
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        <div className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-faint">{t('sidebar.myProjects')}</div>
        {projects.map((p) => {
          const on = p.id === activeId;
          return (
            <button
              key={p.id}
              onClick={() => nav(`/projects/${p.id}`)}
              title={p.title || t('sidebar.untitled')}
              className={cn(
                'relative mb-0.5 block w-full rounded-md py-2 pl-3 pr-2.5 text-left transition-colors',
                on ? 'bg-selected' : 'hover:bg-foreground/[0.04]',
              )}
            >
              {on && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />}
              <div className={cn('truncate text-[13px] font-medium', on ? 'text-foreground' : 'text-muted-foreground')}>
                {p.title || t('sidebar.untitled')}
              </div>
              {p.production_type && (
                <div className="truncate text-[11px] text-faint">{productionTypeMeta(t, p.production_type).label}</div>
              )}
            </button>
          );
        })}
        {nextCursor && (
          <button
            className="mt-1 inline-flex items-center gap-1.5 px-2.5 text-[11px] text-faint underline-offset-2 hover:underline disabled:opacity-60"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore && <Spinner />}
            {t('action.loadMore', { ns: 'common' })}
          </button>
        )}
        {!isLoading && projects.length === 0 && (
          <div className="px-2.5 text-[11px] text-faint">{search ? t('sidebar.noMatch') : t('sidebar.empty')}</div>
        )}
      </div>
    </aside>
  );
}
